
import React from 'react';
import { useFormContext } from '@/contexts/FormContext';
import { useDealerSession } from '@/contexts/DealerSessionContext';
import VehicleMessage from './VehicleMessage';
import SimilarVehiclesMessage from './SimilarVehiclesMessage';
import ServiceFormMessage from './ServiceFormMessage';
import InventoryCountMessage from './InventoryCountMessage';
import FormSubmissionRenderer from './FormSubmissionRenderer';
import ReplyOptionsMessage from './ReplyOptionsMessage';
import InventoryCards from './InventoryCards';
import CarfaxReportMessage from './CarfaxReportMessage';
import CarfaxButtonMessage from './CarfaxButtonMessage';
import OpeningHoursMessage from './OpeningHoursMessage';
import ShowroomMessage from './ShowroomMessage';
import ShowroomCards from './ShowroomCards';
import TextMessage from './TextMessage';
import TextWithLinksMessage from './TextWithLinksMessage';
import PromotionsListMessage from './PromotionsListMessage';
import YouTubeVideoViewer from './YouTubeVideoViewer';
import OEMContentMessage from './OEMContentMessage';
import GoogleMapsLinkMessage from './GoogleMapsLinkMessage';
import { decodeText } from '@/utils/textDecoding';

interface MessagePartRendererProps {
  part: {
    type: 'text' | 'text_with_links' | 'vehicle' | 'similar_vehicles' | 'service_form' | 'close_form' | 'inventory_count' | 'form_submission' | 'reply_options' | 'vehicles_list' | 'carfax_report' | 'carfax_button' | 'opening_hours' | 'showroom' | 'suggested_models' | 'promotions_list' | 'youtube_videos' | 'youtube_video' | 'oem_content' | 'google_maps';
    content: any;
  };
  isUser: boolean;
  onSendMessage: (message: string) => void;
  messageTimestamp?: Date | number; // Pass message timestamp to components that need it
}

const MessagePartRenderer: React.FC<MessagePartRendererProps> = ({ part, isUser, onSendMessage, messageTimestamp }) => {
  const { activeForm } = useFormContext();
  const { sessionData } = useDealerSession();
  const videosEnabled = sessionData?.videosEnabled !== false;
  
  try {
    switch (part.type) {
      case 'vehicle':
        return <VehicleMessage vehicle={part.content} />;
      
      case 'vehicles_list':
        return <InventoryCards vehicles={part.content || []} />;
      
      case 'similar_vehicles':
        // Don't show "View our inventory" button for Service forms
        if (activeForm === 'Service') {
          return null;
        }
        return <SimilarVehiclesMessage data={part.content} />;
      
      case 'service_form':
        return <ServiceFormMessage data={part.content} messageTimestamp={messageTimestamp} />;
      
      case 'close_form':
        // Close form doesn't render anything - it just triggers the minimization via handleFormLogic
        return null;
      
      case 'inventory_count':
        return <InventoryCountMessage data={part.content} />;
      
      case 'form_submission':
        // Add null check for form submission content
        if (!part.content || typeof part.content !== 'object') {
          console.error('Invalid form submission content:', part.content);
          return null;
        }
        
        // Extract formType from the content
        const formType = part.content.formType;
        
        return <FormSubmissionRenderer formData={part.content} formType={formType} />;
      
      case 'reply_options':
        // Extract options from the content structure
        let options = part.content.reply_options || part.content.Reply_Options || [];
        
        // If options is an object (like {Option1: "Sales", Option2: "Service"}), wrap it in an array
        if (!Array.isArray(options) && typeof options === 'object' && options !== null) {
          options = [options];
        }
        
        return <ReplyOptionsMessage options={options} onOptionClick={onSendMessage} />;
      
      case 'carfax_report':
        return (
          <CarfaxReportMessage 
            vehicleData={part.content.vehicleData || {}}
            carfaxUrl={part.content.carfaxUrl || ''}
          />
        );
      
      case 'carfax_button':
        return (
          <CarfaxButtonMessage 
            carfaxUrl={part.content.carfaxUrl || ''}
          />
        );
      
      case 'opening_hours':
        return <OpeningHoursMessage openingHours={part.content || []} singleColumn={true} showTitle={true} />;
      
      case 'showroom':
        return <ShowroomMessage showroom={part.content || {}} />;
      
      case 'suggested_models':
        return <ShowroomCards models={part.content || []} />;
      
      case 'promotions_list':
        // Show title for promotions when in Service form context
        const showPromotionsTitle = activeForm === 'Service';
        return <PromotionsListMessage promotions={part.content || []} showTitle={showPromotionsTitle} />;
      
      case 'youtube_videos':
        if (!videosEnabled) return null;
        return (
          <div>
            {part.content.text && <TextMessage content={part.content.text} isUser={isUser} />}
            {part.content.videos.map((video: {url: string, buttonText: string}, index: number) => (
              <YouTubeVideoViewer 
                key={index}
                videoUrl={video.url} 
                buttonText={decodeText(video.buttonText)} 
              />
            ))}
          </div>
        );
      
      case 'youtube_video':
        if (!videosEnabled) return null;
        return (
          <YouTubeVideoViewer 
            videoUrl={part.content.url} 
            buttonText={decodeText(part.content.buttonText || 'Watch Video')} 
          />
        );
      
      case 'oem_content':
        return (
          <OEMContentMessage 
            images={part.content.image_url || []} 
            summary={part.content.summary}
            {...(part.content["360_images"] && { "360_images": part.content["360_images"] })}
            {...(part.content.brochure_url && { brochure_url: part.content.brochure_url })}
          />
        );
      
      case 'google_maps':
        return <GoogleMapsLinkMessage content={part.content} />;
      
      case 'text_with_links':
        return <TextWithLinksMessage content={part.content} isUser={isUser} />;
      
      case 'text':
      default:
        return <TextMessage content={part.content} isUser={isUser} />;
    }
  } catch (error) {
    console.error('Error rendering message part:', error);
    return <TextMessage content="Error displaying message content" isUser={isUser} />;
  }
};

export default MessagePartRenderer;
